import { REACT_APP_SITE_KEY } from '../Constants';

/**
 * reCAPTCHA v3.
 *
 * The site key is a v3 key and the API verifies the token by reading `score` and comparing it
 * against a threshold, which only a v3 response carries. The sign-up forms, however, rendered the
 * v2 checkbox from `react-google-recaptcha` (the package name is misleading: its version 3 is
 * still the v2 widget). Google answered that pairing with "ERROR for site owner: Invalid key
 * type" printed inside the box, no token was ever produced, and nobody could create an account.
 *
 * v3 has no widget. The script is loaded once, `execute` is called when the form is submitted,
 * and the token goes to the API exactly as before. Google shows its own badge instead.
 */

const SCRIPT_ID = 'google-recaptcha-v3';

let loading = null;

function loadRecaptcha() {
    if (loading) {
        return loading;
    }
    loading = new Promise((resolve, reject) => {
        // Already there, for instance after navigating between the two sign-up forms.
        if (window.grecaptcha && window.grecaptcha.execute) {
            resolve(window.grecaptcha);
            return;
        }
        let script = document.getElementById(SCRIPT_ID);
        const alreadyRequested = script !== null;
        if (!alreadyRequested) {
            script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + REACT_APP_SITE_KEY;
            script.async = true;
        }
        script.addEventListener('load', () => resolve(window.grecaptcha));
        script.addEventListener('error', () => {
            // Cleared so a later attempt can try again rather than being stuck on this failure.
            loading = null;
            reject(new Error('reCAPTCHA could not be loaded'));
        });
        if (!alreadyRequested) {
            document.head.appendChild(script);
        }
    });
    return loading;
}

/**
 * Resolves with a token to send to the API. Rejects if the script cannot load or Google declines
 * to issue one, which the caller should report rather than submitting without it: the API refuses
 * an empty token, and silently doing nothing is what the v2 form did when the box was not ticked.
 */
export async function executeRecaptcha(action) {
    const grecaptcha = await loadRecaptcha();
    return new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
            grecaptcha.execute(REACT_APP_SITE_KEY, { action })
                .then(resolve)
                .catch(reject);
        });
    });
}

export default executeRecaptcha;
